import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout/Layout';
import Modal from '../../../components/Modal/Modal';
import api from '../../../api/axios';
import { fmtDate, fmtEUR, endOfCurrentMonth } from '../../../utils/download';

function UserStatus({ u }) {
  if (u.status === 'ended') {
    return (
      <div className="cell-meta">
        <span className="badge badge-failed" style={{ alignSelf: 'flex-start' }}>Beendet {fmtDate(u.ended_at)}</span>
        <span>{u.runningLicenses > 0 ? `${u.runningLicenses} Kurslizenz(en) laufen noch` : 'Löschung 30 Tage nach Lizenzende'}</span>
      </div>
    );
  }
  if (u.status === 'ending') {
    return <span className="badge badge-pending">Endet {fmtDate(u.termination_at)}</span>;
  }
  return <span className="badge badge-passed">Aktiv</span>;
}

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [resetModal, setResetModal] = useState({ open: false, name: '', password: '' });
  const [frame, setFrame] = useState(null); // { limit, used, licenseCostPerUser }
  const [extendModal, setExtendModal] = useState(false);
  const [newLimit, setNewLimit] = useState('');
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState('');
  const [editUser, setEditUser] = useState(null); // { id, name, email }
  const [editError, setEditError] = useState('');
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { fetchUsers(); fetchFrame(); }, []);

  async function fetchUsers() {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } finally { setLoading(false); }
  }

  async function fetchFrame() {
    try {
      const res = await api.get('/users/license-frame');
      setFrame(res.data);
    } catch { /* Anzeige ist optional */ }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true); setError(''); setLimitReached(false);
    try {
      await api.post('/users', form);
      setModalOpen(false);
      setForm({ name: '', email: '' });
      setMsg('Mitarbeiter angelegt. Die Zugangslizenz wird ab heute anteilig berechnet.');
      fetchUsers();
      fetchFrame();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Anlegen');
      if (err.response?.data?.code === 'LICENSE_LIMIT_REACHED') setLimitReached(true);
    } finally { setSaving(false); }
  }

  async function handleEdit(e) {
    e.preventDefault();
    setSaving(true); setEditError('');
    try {
      const res = await api.put(`/users/${editUser.id}`, { name: editUser.name, email: editUser.email });
      setEditUser(null);
      setMsg(res.data?.message || 'Mitarbeiter aktualisiert');
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  }

  async function handleReset(user) {
    if (!window.confirm(`Passwort für "${user.name}" zurücksetzen?`)) return;
    try {
      const res = await api.post(`/users/${user.id}/reset-password`);
      setResetModal({ open: true, name: user.name, password: res.data.newPassword });
    } catch { alert('Fehler beim Zurücksetzen'); }
  }

  async function handleTerminate(user) {
    const effective = fmtDate(endOfCurrentMonth());
    const ok = window.confirm(
      `"${user.name}" zur Beendigung vormerken?\n\n` +
      `Der Zugang bleibt bis zum ${effective} (Ende des laufenden Kalendermonats) nutzbar und endet dann automatisch (AGB § 5 (6) g). ` +
      'Die Zugangslizenz wird für diesen Monat noch berechnet. Laufende Kurslizenzen werden gemäß AGB § 5 (6) d/e behandelt. ' +
      'Die Kontodaten werden 30 Tage nach Ablauf der letzten Lizenz gelöscht.\n\n' +
      'Die Vormerkung kann bis zum Wirkungszeitpunkt widerrufen werden. Sie erhalten eine Bestätigung per E-Mail.'
    );
    if (!ok) return;
    setBusyId(user.id);
    try {
      const res = await api.post(`/users/${user.id}/terminate`);
      setMsg(res.data?.message || 'Beendigung vorgemerkt');
      fetchUsers();
      fetchFrame();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Vormerken');
    } finally { setBusyId(null); }
  }

  async function handleRevoke(user) {
    if (!window.confirm(`Vormerkung für "${user.name}" aufheben? Der Zugang läuft dann unverändert weiter.`)) return;
    setBusyId(user.id);
    try {
      const res = await api.post(`/users/${user.id}/terminate/revoke`);
      setMsg(res.data?.message || 'Vormerkung aufgehoben');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Aufheben');
    } finally { setBusyId(null); }
  }

  function openExtend() {
    setNewLimit(frame ? String(frame.limit + 5) : '');
    setExtendError('');
    setExtendModal(true);
  }

  async function handleExtend(e) {
    e.preventDefault();
    const limit = parseInt(newLimit, 10);
    if (Number.isNaN(limit) || !frame || limit <= frame.limit) {
      setExtendError(`Der neue Rahmen muss größer als ${frame?.limit ?? 0} sein.`);
      return;
    }
    if (!window.confirm('Hinweis: Durch diese Erweiterung wird das Kontingent kostenpflichtig erhöht (siehe AGB und Einzelauftrag).')) return;
    setExtending(true); setExtendError('');
    try {
      await api.put('/users/license-frame', { limit });
      setExtendModal(false);
      setLimitReached(false);
      fetchFrame();
    } catch (err) {
      setExtendError(err.response?.data?.message || 'Fehler beim Erweitern');
    } finally {
      setExtending(false);
    }
  }

  const frameFull = frame && frame.used >= frame.limit;
  const addedLicenses = frame ? Math.max(0, (parseInt(newLimit, 10) || 0) - frame.limit) : 0;

  return (
    <Layout title="Mitarbeiter">
      {frame && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
          background: frameFull ? 'rgba(239,68,68,0.08)' : 'var(--white)',
          border: `1px solid ${frameFull ? 'var(--error)' : 'var(--gray)'}`,
          borderRadius: 8, padding: '0.85rem 1.25rem', marginBottom: '1.25rem',
        }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.35rem' }}>
              Lizenzrahmen: {frame.used} von {frame.limit} Lizenzen vergeben
              {frameFull && <span style={{ color: 'var(--error)', marginLeft: '0.5rem' }}>(Rahmen ausgeschöpft)</span>}
            </div>
            <div style={{ background: 'var(--light)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
              <div style={{
                width: '100%',
                transform: `scaleX(${Math.min(1, frame.used / Math.max(1, frame.limit))})`,
                transformOrigin: 'left center',
                background: frameFull ? 'var(--error)' : 'var(--primary)',
                height: '100%', transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              }} />
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={openExtend}>Rahmen erweitern</button>
        </div>
      )}

      {msg && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{msg}</div>}

      <div className="page-header">
        <div />
        <button className="btn btn-primary btn-sm" onClick={() => { setModalOpen(true); setError(''); setLimitReached(false); }}>
          + Neuen Mitarbeiter anlegen
        </button>
      </div>

      {loading ? <div>Laden...</div> : (
        <div className="table-container">
          <table>
            <thead>
              <tr><th>Mitarbeiter</th><th>Status</th><th>Schulungen</th><th>Angelegt</th><th>Aktionen</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ opacity: u.status === 'ended' ? 0.6 : 1 }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{u.name}</span>
                      {u.role === 'customer_admin' && <span className="badge badge-in_progress" style={{ fontSize: '0.7rem', padding: '0.1rem 0.5rem' }}>Admin</span>}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', overflowWrap: 'anywhere' }}>{u.email}</div>
                  </td>
                  <td><UserStatus u={u} /></td>
                  <td>{u.trainingCount}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(u.created_at)}</td>
                  <td>
                    <div className="actions-row">
                      {u.status !== 'ended' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => { setEditUser({ id: u.id, name: u.name, email: u.email }); setEditError(''); }}>Bearbeiten</button>
                      )}
                      {u.status === 'active' && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleReset(u)}>Passwort zurücksetzen</button>
                          <button className="btn btn-ghost btn-ghost-danger btn-sm" disabled={busyId === u.id} onClick={() => handleTerminate(u)}>
                            {busyId === u.id ? 'Wird vorgemerkt…' : 'Beendigung vormerken'}
                          </button>
                        </>
                      )}
                      {u.status === 'ending' && (
                        <button className="btn btn-ghost btn-sm" disabled={busyId === u.id} onClick={() => handleRevoke(u)}>
                          {busyId === u.id ? 'Wird aufgehoben…' : 'Vormerkung aufheben'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-dark)' }}>Noch keine Mitarbeiter</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Neuen Mitarbeiter anlegen">
        <form onSubmit={handleCreate}>
          {error && (
            <div className="alert alert-error">
              {error}
              {limitReached && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ display: 'block', marginTop: '0.5rem' }}
                  onClick={() => { setModalOpen(false); openExtend(); }}
                >
                  Lizenzrahmen erweitern
                </button>
              )}
            </div>
          )}
          <div className="form-group">
            <label>Name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Max Mustermann" />
          </div>
          <div className="form-group">
            <label>E-Mail-Adresse</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="mitarbeiter@firma.de" />
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
            Der Mitarbeiter erhält automatisch eine E-Mail mit seinen Zugangsdaten.
            {frame && <> Jedes Benutzerkonto belegt eine Lizenz ({frame.used} von {frame.limit} vergeben).</>}
            {' '}Die Zugangslizenz wird im Anlagemonat anteilig ab heute berechnet.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setModalOpen(false)}>Abbrechen</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Anlegen...' : 'Anlegen'}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!editUser} onClose={() => setEditUser(null)} title="Kontodaten ändern">
        {editUser && (
          <form onSubmit={handleEdit}>
            {editError && <div className="alert alert-error">{editError}</div>}
            <div className="form-group">
              <label>Name</label>
              <input value={editUser.name} onChange={e => setEditUser({ ...editUser, name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>E-Mail-Adresse</label>
              <input type="email" value={editUser.email} onChange={e => setEditUser({ ...editUser, email: e.target.value })} required />
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
              Name und E-Mail derselben Person können jederzeit geändert werden, ohne dass eine neue Lizenz entsteht.
              Ein Konto kann nicht auf eine andere Person umgeschrieben werden. Legen Sie dafür bitte ein neues Konto an.
              Jede Änderung wird protokolliert.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditUser(null)}>Abbrechen</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Speichern...' : 'Speichern'}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={extendModal} onClose={() => setExtendModal(false)} title="Lizenzrahmen erweitern" size="sm">
        <form onSubmit={handleExtend}>
          {extendError && <div className="alert alert-error">{extendError}</div>}
          <p style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
            Ihr aktueller Rahmenvertrag umfasst <strong>{frame?.limit} Lizenzen</strong>,
            davon sind <strong>{frame?.used} vergeben</strong>.
          </p>
          <div className="form-group">
            <label>Neuer Lizenzrahmen</label>
            <input
              type="number"
              min={(frame?.limit || 0) + 1}
              value={newLimit}
              onChange={e => setNewLimit(e.target.value)}
              required
            />
          </div>
          {addedLicenses > 0 && (
            <div style={{ background: 'var(--light)', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.85rem', marginBottom: '1rem' }}>
              + {addedLicenses} zusätzliche Lizenz(en)
              {frame?.licenseCostPerUser > 0 && (
                <>, bis zu <strong>{fmtEUR(addedLicenses * frame.licenseCostPerUser)}</strong> zusätzliche Lizenzkosten pro Monat, sobald die Lizenzen vergeben sind</>
              )}
            </div>
          )}
          <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
            Die Erweiterung dient Ihrer eigenen Kostenkontrolle und kann nur durch den Anbieter wieder reduziert werden.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExtendModal(false)}>Abbrechen</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={extending}>
              {extending ? 'Erweitern...' : 'Rahmen erweitern'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={resetModal.open} onClose={() => setResetModal({ open: false, name: '', password: '' })} title="Neues Passwort" size="sm">
        <p style={{ marginBottom: '1rem' }}>Das neue Passwort für <strong>{resetModal.name}</strong>:</p>
        <div style={{ background: 'var(--light)', padding: '1rem', borderRadius: 8, fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, textAlign: 'center', color: 'var(--primary-dark)', marginBottom: '1rem' }}>
          {resetModal.password}
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
          Der Mitarbeiter hat ebenfalls eine E-Mail mit dem neuen Passwort erhalten.
        </p>
        <button className="btn btn-primary btn-sm btn-full" onClick={() => setResetModal({ open: false, name: '', password: '' })}>Schließen</button>
      </Modal>
    </Layout>
  );
}
